import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { TransportationPaymentsStatus } from '@features/transportation-payments/enums/transportation-payments-status.enum';
import { DateRangeType } from '@shared/enums/date-range-type.enum';
import { TabIndex } from '@shared/enums/tab-index.enum';
import { TabGroup } from '@shared/enums/tab-group.enum';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ConfigConstant } from '@shared/constants/config.constant';
import { MatSort } from '@angular/material/sort';
import { TabModel } from '@shared/model/tab.model';
import { FilterService } from '@shared/services/filter.service';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { SharedConstants } from '@shared/model/shared-constants';
import { handleSortChange } from '@shared/utils/handle-sort-change.util';
import { TransportationPaymentsCommonService } from '@shared-features/services/transportation-payments-common.service';
import { Shipment } from '@shared-features/models/shipment';
import { InvoiceTransportation } from '@features/invoices/models/invoice-transportation';
import { InvoiceTransportationDetails } from '@features/invoices/models/invoice-transportation-details';
import { InvoicesStatus } from '@features/invoices/enums/invoices-status.enum';
import { ColorPalette } from '@shared/enums';

@Component({
  selector: 'app-payments-list',
  templateUrl: './payments-list.component.html',
  styleUrls: ['./payments-list.component.scss'],
})
export class PaymentsListComponent extends BaseComponent implements OnInit {
  dataSource = new MatTableDataSource<InvoiceTransportation>();
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  sectionState: SectionStateStatus;
  sectionStateStatus = SectionStateStatus;
  invoiceStatus: InvoicesStatus;

  public displayedColumns: string[] = [
    'id',
    'createdBy',
    'createdAt',
    'paymentDate',
    'bankRef',
    'from',
    'to',
    'total',
    'paymentStatus',
    'action',
  ];
  colorPerStatus: { [n: string]: string } = {
    [TransportationPaymentsStatus.PAID]: ColorPalette.Success,
    [TransportationPaymentsStatus.REJECTED]: ColorPalette.Accent,
    [TransportationPaymentsStatus.PENDING]: ColorPalette.Default,
  };

  customerId: any = null;
  createdBy: any = null;
  currentIndex = 0;
  selectedIndex = 0;
  currentTabIndex: number;

  transportationPaymentsStatus: TransportationPaymentsStatus;
  Status = TransportationPaymentsStatus;

  tabs: TabModel[] = [
    {
      label: TransportationPaymentsStatus.ALL,
      showCount: true,
    },
    {
      label: TransportationPaymentsStatus.PENDING,
      showCount: true,
    },
    {
      label: TransportationPaymentsStatus.REJECTED,
      showCount: true,
    },
    {
      label: TransportationPaymentsStatus.PAID,
      showCount: true,
    },
  ];

  filterNavigationConfig: TabModel = {
    group: TabGroup.TransportationPayments,
    index: TabIndex.Payments,
  };

  selectedElement: Shipment = new Shipment();
  @ViewChild('previewMarkModal')
  previewMarkModal: ModalComponent;
  @Output() exportClicked = new EventEmitter<void>();

  actionType: string;
  paymentDetails: InvoiceTransportationDetails = new InvoiceTransportationDetails();
  // urlParamsForFiltration: any = null;
  fleetId: number;
  fleetName: string;

  constructor(
    private filterService: FilterService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private transportationPaymentsCommonService: TransportationPaymentsCommonService
  ) {
    super();
  }

  ngOnInit(): void {
    const { tabIndex } = this.activatedRoute.snapshot.queryParams;
    this.getFleetData();
    this.filters.fields = {
      paymentStatus: null,
      customerId: null,
      creatorId: null,
      fleetId: this.fleetId,
      tabGroup: TabGroup.TransportationPayments,
    };
    this.filters.params = { dateRangeType: DateRangeType.ALL_TIME };
    this.selectedIndex = +tabIndex || 0;
    this.currentIndex = +tabIndex || 0;
    this.subscribeToFilterChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getPayments(): void {
    const { creatorId, paymentStatus, customerId, ...params } = this.filters.params;
    this.load(
      this.transportationPaymentsCommonService.getInvoicesTransportation({
        ...params,
        paymentStatus,
        createdBy: SharedConstants.ALL === creatorId ? null : creatorId,
        customerId: SharedConstants.ALL === customerId ? null : customerId,
      }),
      {
        contentPath: 'invoiceTransportationPayment',
      }
    ).subscribe((res) => {
      this.paymentDetails = res;
      this.setupTabsCount();
      switch (this.filters.params.paymentStatus) {
        case null:
          this.setupTable(
            this.paymentDetails.invoiceTransportationPayment,
            this.paymentDetails.count
          );
          break;

        case TransportationPaymentsStatus.PENDING:
          this.setupTable(
            this.paymentDetails.invoiceTransportationPayment,
            this.paymentDetails.pendingCount
          );
          break;

        case TransportationPaymentsStatus.REJECTED:
          this.setupTable(
            this.paymentDetails.invoiceTransportationPayment,
            this.paymentDetails.rejectedCount
          );
          break;

        case TransportationPaymentsStatus.PAID:
          this.setupTable(
            this.paymentDetails.invoiceTransportationPayment,
            this.paymentDetails.paidCount
          );
          break;
      }
    });
  }

  getFleetData(): void {
    this.fleetId = Number(this.activatedRoute.snapshot.params?.fleetId) || null;
    this.fleetName = this.activatedRoute.snapshot.params?.fleetName || '';
  }

  subscribeToFilterChanges(): void {
    this.filters.data$.pipe(takeUntil(this.destroy$), debounceTime(250)).subscribe((params) => {
      const { dateRangeType, startFrom, endAt } = this.filters.params;
      if (dateRangeType !== DateRangeType.CUSTOM || (startFrom && endAt)) {
        this.getPayments();
      }
    });

    this.filterService.URLParams$.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params && params?.tabGroup === TabGroup.TransportationPayments) {
        const { dateRange, ...restOfParams } = params;
        this.filters.params = { ...restOfParams, dateRangeType: dateRange };
      }
    });
  }

  setupTable(data: InvoiceTransportation[], count: number): void {
    this.dataSource.data = data;
    this.totalRowsCount = count;

    if (!count) {
      this.sectionState = SectionStateStatus.Empty;
    } else {
      this.sectionState = SectionStateStatus.Ready;
    }
  }

  onTabIndexChange(event: number): void {
    this.currentIndex = event;
    const paymentStatus = this.getStatus();
    this.setFilterParams({ paymentStatus, offset: ConfigConstant.INIT_PAGE_OFFSET }, null, true);
  }

  getStatus(): TransportationPaymentsStatus {
    let status = null;
    switch (this.currentIndex) {
      case 0:
        status = null;
        break;

      case 1:
        status = TransportationPaymentsStatus.PENDING;
        break;

      case 2:
        status = TransportationPaymentsStatus.REJECTED;
        break;

      case 3:
        status = TransportationPaymentsStatus.PAID;
        break;
    }
    return status;
  }

  setupTabsCount(): void {
    this.tabs = this.tabs.map((tab: TabModel) => {
      switch (tab.label) {
        case TransportationPaymentsStatus.ALL:
          tab.count = this.paymentDetails.totalCount;
          break;

        case TransportationPaymentsStatus.PENDING:
          tab.count = this.paymentDetails.pendingCount;
          break;

        case TransportationPaymentsStatus.REJECTED:
          tab.count = this.paymentDetails.rejectedCount;
          break;

        case TransportationPaymentsStatus.PAID:
          tab.count = this.paymentDetails.paidCount;
          break;
      }
      return tab;
    });
  }

  onPreviewMarkModal(element: Shipment, type: string): void {
    this.selectedElement = element;
    this.actionType = type;
    this.previewMarkModal.showModal();
  }

  reloadComponent(): void {
    this.getPayments();
  }

  navigateToDetailsPage(id: number): void {
    if (this.fleetId) {
      this.router.navigate([
        RoutesUtil.TransPaysPaymentsViewWithFleet.url({
          fleetId: this.fleetId,
          fleetName: this.fleetName,
          invoiceId: id,
        }),
      ]);
    } else {
      this.router.navigate([RoutesUtil.TransPaysPaymentsView.url({ invoiceId: id })]);
    }
  }

  onSortChange(value): void {
    this.setFilterParams(handleSortChange(value), null, true);
  }

  setFilterParams(params, displays?: any, updateRouteParams?: boolean): void {
    this.filters.params = params;
    if (displays) {
      this.filters.display = displays;
    }

    if (updateRouteParams) {
      this.filterService.navigateToTargetPageWithFilter({
        ...this.filters.sourceOfAllParams,
        tabIndex: this.currentIndex,
      });
    }
  }
  onExportClicked(): void {
    this.exportClicked.emit();
  }
}
